/**
 * 3-Tier Dynamic Pricing Matrix for Hamim Enterprises
 * Clean English-only format: Normal, Premium, VIP
 * No percentage margin badges (-10%, +20%) and no "Hospital Official" tags as per user request.
 */

export type PricingTierKey = 'normal' | 'premium' | 'vip';

export interface PriceTierInfo {
  key: PricingTierKey;
  labelEn: string;
  badge: string;
  priceUsd: number;
  description: string;
}

export interface ProductTierMatrix {
  basePrice: number;
  normal: PriceTierInfo;
  premium: PriceTierInfo;
  vip: PriceTierInfo;
  vipMarginRate: number; // 0.10, 0.15, or 0.20
}

/**
 * Calculates the VIP margin percentage based on instrument value:
 * Higher value / expensive instruments get 15-20% margin, common ones get 10-15%.
 */
export function getVipMarginPercent(basePrice: number): number {
  if (basePrice >= 150) return 20; // Expensive instruments
  if (basePrice >= 60) return 15;  // Mid-tier instruments
  return 10;                       // Common / standard instruments
}

/**
 * Calculates all 3 pricing tiers for an instrument.
 */
export function calculateProductTiers(item: {
  price: number;
  normalPrice?: number | null;
  premiumPrice?: number | null;
  vipPrice?: number | null;
  vipMargin?: number | null;
}): ProductTierMatrix {
  const basePrice = Math.max(0, Number(item.price) || 0);

  // VIP margin calculation (10%, 15%, or 20%)
  const vipMarginPercent = item.vipMargin !== undefined && item.vipMargin !== null 
    ? Number(item.vipMargin) 
    : getVipMarginPercent(basePrice);

  // 1. Normal: 10% below official price
  const calculatedNormal = Math.round(basePrice * 0.90 * 100) / 100;
  const normalPrice = item.normalPrice !== undefined && item.normalPrice !== null && item.normalPrice > 0 
    ? Number(item.normalPrice) 
    : calculatedNormal;

  // 2. Premium: Official base price
  const premiumPrice = item.premiumPrice !== undefined && item.premiumPrice !== null && item.premiumPrice > 0 
    ? Number(item.premiumPrice) 
    : basePrice;

  // 3. VIP: 10% to 20% margin up
  const calculatedVip = Math.round(basePrice * (1 + vipMarginPercent / 100) * 100) / 100;
  const vipPrice = item.vipPrice !== undefined && item.vipPrice !== null && item.vipPrice > 0 
    ? Number(item.vipPrice) 
    : calculatedVip;

  return {
    basePrice,
    vipMarginRate: vipMarginPercent,
    normal: {
      key: 'normal',
      labelEn: 'Normal',
      badge: 'Normal',
      priceUsd: normalPrice,
      description: 'Standard surgical grade finish, calibrated for general clinic and bulk surgical procedures.'
    },
    premium: {
      key: 'premium',
      labelEn: 'Premium',
      badge: 'Premium',
      priceUsd: premiumPrice,
      description: 'Official Hamim Enterprises certified surgical instrument, calibrated for sterile operating suites.'
    },
    vip: {
      key: 'vip',
      labelEn: 'VIP',
      badge: 'VIP',
      priceUsd: vipPrice,
      description: 'Master surgeon edition with high-durability passivation and surgical grade calibration.'
    }
  };
}

/**
 * Returns formatted prices in USD, PKR, and CNY for a given tier price.
 */
export function formatTierMultiCurrency(
  priceUsd: number, 
  pkrRate = 280, 
  cnyRate = 7.2
): { usd: string; pkr: string; cny: string } {
  const pkr = Math.round(priceUsd * pkrRate);
  const cny = Math.round(priceUsd * cnyRate * 10) / 10;

  return {
    usd: `$${priceUsd.toFixed(2)}`,
    pkr: `Rs. ${pkr.toLocaleString()}`,
    cny: `¥${cny.toFixed(1)}`
  };
}
